import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { IconButton } from '../ds';
import { Eyebrow } from './primitives';

/**
 * Bottom sheet — the container the add-entry sheet and the smaller editors
 * share. Scrim fades in, panel rises with the soft settle ease.
 *
 * Unlike the prototype's fixed 812px frame, a real sheet can outgrow the
 * viewport (the sleep variant on a small phone, plus a keyboard), so the body
 * scrolls inside the panel and the panel is height-capped.
 */

export function Sheet({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  eyebrow?: ReactNode;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape closes, and the page behind must not scroll while a sheet is up.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  // Move focus into the sheet so a keyboard or screen reader follows it.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 40, display: 'flex', justifyContent: 'center' }}>
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--overlay)',
          backdropFilter: 'blur(2px)',
          animation: 'scrimIn .2s ease',
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          margin: '0 auto',
          width: '100%',
          maxWidth: 520,
          maxHeight: '92dvh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          boxShadow: 'var(--shadow-xl)',
          animation: 'sheetUp .28s cubic-bezier(0.32,0.72,0.36,1)',
          outline: 'none',
        }}
      >
        <div style={{ padding: '10px 20px 0', flex: 'none' }}>
          <div
            aria-hidden
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: 'var(--border-strong)',
              margin: '0 auto 14px',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              marginBottom: 16,
            }}
          >
            <div style={{ minWidth: 0 }}>
              {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
              <h3
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 'var(--text-xl)',
                  margin: '2px 0 0',
                }}
              >
                {title}
              </h3>
            </div>
            <IconButton icon="X" label="閉じる" variant="ghost" onClick={onClose} />
          </div>
        </div>

        <div className="scroll-area" style={{ flex: 1, minHeight: 0, padding: '0 20px' }}>
          {children}
        </div>

        {footer ? (
          <div
            style={{
              flex: 'none',
              padding: '12px 20px calc(24px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            {footer}
          </div>
        ) : (
          <div style={{ height: 'calc(16px + env(safe-area-inset-bottom, 0px))' }} />
        )}
      </div>
    </div>
  );
}

/** Divider-topped row used throughout the sheets: label on the left, control right. */
export function SheetRow({
  label,
  children,
  first = false,
}: {
  label: ReactNode;
  children: ReactNode;
  first?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: '13px 0',
        borderTop: first ? 'none' : '1px solid var(--border)',
      }}
    >
      <div style={{ minWidth: 0 }}>{label}</div>
      {children}
    </div>
  );
}
